import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/admin.css';
import { authFetch } from '../services/auth';
import { useMe } from '../store/useMe';

interface AdminOrder {
  orderId: string;
  invoiceId: string;
  status: string;
  paymentMethod: string;
  isPaid: boolean;
  orderDate: string;
  customer: {
    customerId: string;
    email: string;
    name: string;
  };
  totalAmount: {
    amount: number;
    currency: string;
  };
  itemCount: number;
  notes?: string;
  assigneeId?: string | null;
  assigneeName?: string | null;
  assigneeAvatarUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  packageName?: string | null;
  sampleLocation?: string | null;
  sampleDate?: string | null;
  hasResult?: boolean;
  orderNumber?: string | null;
  reference?: string | null;
}

interface UserLite {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
}

interface AdminOrdersResponse {
  orders: AdminOrder[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// One Kanban column, paginated independently from the others.
interface BoardColumn {
  orders: AdminOrder[];
  total: number;
  page: number;
  loadingMore: boolean;
}

// How many cards each board column loads per request.
const BOARD_PAGE_SIZE = 15;

interface Money {
  amount: number;
  currency: string;
}

interface OrderDetails {
  orderId: string;
  invoiceId: string;
  status: string;
  paymentMethod: string;
  isPaid: boolean;
  orderDate: string;
  customer: {
    customerId: string;
    email: string;
    name: string;
    phone?: string | null;
    company?: string | null;
    legalAddress?: string | null;
  };
  items: {
    lineId: string;
    packageId: string;
    packageName: string;
    quantity: number;
    unitPrice: Money;
    totalPrice: Money;
    selectedOptions: string[];
    specialInstructions?: string | null;
    indicators?: { group: string; items: string[] }[];
  }[];
  pricing: {
    originalPrice: Money;
    discountAmount: Money;
    finalPrice: Money;
  };
  appliedDiscountCodes: string[];
  notes?: string | null;
  sampleSource?: string | null;
  sampleGoal?: string | null;
  sampleLocation?: string | null;
  sampleDate?: string | null;
  amountDue: Money;
  createdAt: string;
  updatedAt: string;
  assigneeName?: string | null;
  orderNumber?: string | null;
  reference?: string | null;
  history?: OrderEvent[];
  comments?: OrderComment[];
  result?: OrderResult | null;
  act?: OrderAct | null;
  customerType?: string;
  b2b?: {
    vatMode?: string | null;
    vatRate: number;
    vatAmount: Money;
    grossAmount: Money;
    legalName: string;
    edrpou: string;
    ipnVat?: string | null;
    legalAddress?: string | null;
    contactPerson?: string | null;
  } | null;
}

interface OrderEvent {
  at: string;
  type: string;
  from?: string | null;
  to?: string | null;
  actorName: string;
  note?: string | null;
}

interface OrderComment {
  id: string;
  at: string;
  authorId: string;
  authorName: string;
  text: string;
}

interface OrderResult {
  fileName: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  uploadedByName: string;
}

interface OrderAct {
  fileName: string;
  contentType: string;
  size: number;
  createdAt: string;
  byName: string;
  source: string; // "generated" | "uploaded"
}

interface MethodOption {
  id: string;
  name: string;
  ndd: string;
}

interface MeasurementRow {
  code: string;
  name: string;
  unit: string;
  normText: string;
  method: string;
  methodId?: string;
  methodName?: string;
  methods?: MethodOption[]; // available methodologies; present only when >1
  value?: string | null;
  verdict?: string | null; // "ok" | "out" | null
  measuredByName?: string | null;
  measuredAt?: string | null;
}

interface ProtocolMeta {
  number: string;
  date: string;
  performerPosition: string;
  performerName: string;
  headName: string;
  complies: boolean;
  generatedAt: string;
  generatedByName: string;
}

interface MeasurementSheet {
  orderId: string;
  packageId: string;
  total: number;
  filled: number;
  complete: boolean;
  protocol?: ProtocolMeta | null;
  groups: { group: string; rows: MeasurementRow[] }[];
}

export const AdminOrdersPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [confirmChange, setConfirmChange] = useState<{ orderId: string; status: string; label: string } | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OrderDetails | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'overview' | 'payment' | 'analyses'>('overview');
  const [openIndicators, setOpenIndicators] = useState<Record<string, boolean>>({});
  const [commentText, setCommentText] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const [resultBusy, setResultBusy] = useState(false);
  // Measurement sheet (вкладка «Аналізи»): null until loaded / when none exists.
  const [measurements, setMeasurements] = useState<MeasurementSheet | null>(null);
  const [measCreating, setMeasCreating] = useState(false);
  // Draft input values keyed by indicator code (controlled inputs, saved on blur).
  const [measDrafts, setMeasDrafts] = useState<Record<string, string>>({});
  const [measSavingCode, setMeasSavingCode] = useState<string | null>(null);
  // Selected indicator group inside the analyses tab ('' = first group).
  const [measGroup, setMeasGroup] = useState('');
  // Protocol generation form (shown when the sheet is complete).
  const [protoOpen, setProtoOpen] = useState(false);
  const [protoBusy, setProtoBusy] = useState(false);
  const emptyProtoForm = { number: '', date: '', performerName: '', performerPosition: '', headName: '', complies: 'auto' };
  const [protoForm, setProtoForm] = useState(emptyProtoForm);
  // Staff directory for the signer pickers (available to lab users, unlike /users).
  const [staff, setStaff] = useState<{ id: string; name: string; position?: string | null }[]>([]);
  const { me } = useMe();
  const [users, setUsers] = useState<UserLite[]>([]);
  const [myOnly, setMyOnly] = useState(false);
  // Per-column board state: each status column is paginated independently so the
  // board never loads the whole order history at once.
  const [board, setBoard] = useState<Record<string, BoardColumn>>({});
  // Completed column: collapse orders older than a week behind a toggle so the
  // active pipeline stays in focus.
  const [showOlderCompleted, setShowOlderCompleted] = useState(false);
  // Filters shared by both the board and the table. On phones they collapse
  // behind a toggle so the board starts above the fold.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState(''); // '', 'none', or a userId
  // Manual order creation (admin + lab).
  const emptyCreateForm = {
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    packageId: '',
    price: '',
    quantity: 1,
    paymentMethod: 'invoice',
    isPaid: false,
    notes: '',
  };
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  // Lab packages (same source as the order composition); price is a prefill.
  const [createPackages, setCreatePackages] = useState<
    { id: string; name: string; codes: string[]; price?: number | null }[]
  >([]);
  const [createForm, setCreateForm] = useState(emptyCreateForm);

  useEffect(() => {
    if (me?.isAdmin) {
      authFetch('/api/admin/users')
        .then((r) => (r.ok ? r.json() : { users: [] }))
        .then((d) => setUsers(d.users || []))
        .catch(() => {});
    }
  }, [me?.isAdmin]);

  const userDisplay = (u: UserLite) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;

  const assignOrder = async (orderId: string, assigneeId: string | null) => {
    try {
      const res = await authFetch(`/api/admin/orders/${orderId}/assignee`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigneeId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const updated: AdminOrder = await res.json();
      const apply = (o: AdminOrder): AdminOrder =>
        o.orderId === orderId ? { ...o, assigneeId: updated.assigneeId, assigneeName: updated.assigneeName } : o;
      // Update every board column (cards live in separate per-column state).
      setBoard((prev) => {
        const next: Record<string, BoardColumn> = {};
        for (const [status, col] of Object.entries(prev)) {
          next[status] = { ...col, orders: col.orders.map(apply) };
        }
        return next;
      });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to assign');
    }
  };

  // Sync the sheet into state and refresh the input drafts from saved values.
  const applyMeasurements = (sheet: MeasurementSheet | null) => {
    setMeasurements(sheet);
    setMeasGroup((g) => (sheet && sheet.groups.some((x) => x.group === g) ? g : ''));
    const drafts: Record<string, string> = {};
    sheet?.groups.forEach((g) => g.rows.forEach((r) => { drafts[r.code] = r.value ?? ''; }));
    setMeasDrafts(drafts);
  };

  const loadMeasurements = async (orderId: string) => {
    try {
      const res = await authFetch(`/api/admin/orders/${orderId}/measurements`);
      applyMeasurements(res.ok ? await res.json() : null);
    } catch {
      applyMeasurements(null);
    }
  };

  const openDetail = async (orderId: string) => {
    setDetailId(orderId);
    setDetail(null);
    setDetailTab('overview');
    setOpenIndicators({});
    applyMeasurements(null);
    setDetailLoading(true);
    try {
      const [res] = await Promise.all([authFetch(`/api/orders/${orderId}`), loadMeasurements(orderId)]);
      if (res.ok) setDetail(await res.json());
    } catch {
      /* keep the modal open with a load error state */
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  // Re-fetch the open order so history/comments/result reflect the latest change.
  const reloadDetail = async () => {
    if (!detailId) return;
    try {
      const res = await authFetch(`/api/orders/${detailId}`);
      if (res.ok) setDetail(await res.json());
    } catch {
      /* ignore — keep showing the previous detail */
    }
  };

  // Create the measurement sheet from the ordered package (idempotent server-side).
  const createMeasurementSheet = async () => {
    if (!detailId) return;
    setMeasCreating(true);
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/measurements`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      applyMeasurements(await res.json());
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:measSaveError'));
    } finally {
      setMeasCreating(false);
    }
  };

  // Save one indicator's value on blur/Enter; skips when unchanged.
  const saveMeasurement = async (row: MeasurementRow) => {
    if (!detailId || !measurements) return;
    const draft = (measDrafts[row.code] ?? '').trim();
    if (draft === (row.value ?? '')) return;
    setMeasSavingCode(row.code);
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/measurements/${row.code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: draft || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      applyMeasurements(await res.json());
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:measSaveError'));
    } finally {
      setMeasSavingCode(null);
    }
  };

  // Change the methodology used for one indicator (drives printed ДСТУ + consumption).
  const changeMethod = async (row: MeasurementRow, methodId: string) => {
    if (!detailId) return;
    setMeasSavingCode(row.code);
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/measurements/${row.code}/method`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ methodId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      applyMeasurements(await res.json());
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:measSaveError'));
    } finally {
      setMeasSavingCode(null);
    }
  };

  // Open the protocol form prefilled from the last protocol / assignee / localStorage.
  const openProtoForm = () => {
    if (staff.length === 0) {
      authFetch('/api/admin/staff')
        .then((r) => (r.ok ? r.json() : { staff: [] }))
        .then((d) => setStaff(d.staff || []))
        .catch(() => {});
    }
    const p = measurements?.protocol;
    setProtoForm({
      number: p?.number || '',
      date: (p?.date || new Date().toISOString()).slice(0, 10),
      performerName: p?.performerName || detail?.assigneeName || '',
      performerPosition: p?.performerPosition || 'Лаборант хімічного аналізу',
      headName: p?.headName || localStorage.getItem('emlab_protocol_head') || '',
      complies: 'auto',
    });
    setProtoOpen(true);
  };

  const generateProtocol = async () => {
    if (!detailId) return;
    setProtoBusy(true);
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/protocol/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          number: protoForm.number.trim() || null,
          date: protoForm.date ? new Date(protoForm.date).toISOString() : null,
          performerName: protoForm.performerName.trim() || null,
          performerPosition: protoForm.performerPosition.trim() || null,
          headName: protoForm.headName.trim() || null,
          complies: protoForm.complies === 'auto' ? null : protoForm.complies === 'yes',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const { url } = await res.json();
      if (protoForm.headName.trim()) localStorage.setItem('emlab_protocol_head', protoForm.headName.trim());
      if (url) window.open(url, '_blank', 'noopener');
      setProtoOpen(false);
      await Promise.all([reloadDetail(), loadMeasurements(detailId)]);
    } catch (e) {
      alert(e instanceof Error ? e.message : t('admin:protoError'));
    } finally {
      setProtoBusy(false);
    }
  };

  const submitComment = async () => {
    if (!detailId || !commentText.trim()) return;
    setCommentBusy(true);
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: commentText.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      setCommentText('');
      await reloadDetail();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to add comment');
    } finally {
      setCommentBusy(false);
    }
  };

  const uploadResult = async (file: File) => {
    if (!detailId) return;
    if (file.size > 15 * 1024 * 1024) {
      alert(t('admin:resultTooLarge'));
      return;
    }
    setResultBusy(true);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('read error'));
        reader.readAsDataURL(file);
      });
      const res = await authFetch(`/api/admin/orders/${detailId}/result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, contentType: file.type, contentBase64: dataUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      await reloadDetail();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to upload result');
    } finally {
      setResultBusy(false);
    }
  };

  const downloadResult = async () => {
    if (!detailId) return;
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/result`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url } = await res.json();
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to download result');
    }
  };

  // Рахунок: generated on demand (server returns a presigned URL). Акт: generated
  // once and stored on the order (see generateAct/uploadAct/downloadAct below).
  const [docBusy, setDocBusy] = useState<'invoice' | 'act' | null>(null);
  const [actBusy, setActBusy] = useState(false);

  const downloadDoc = async (kind: 'invoice') => {
    if (!detailId) return;
    setDocBusy(kind);
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/${kind}`);
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b?.message) msg = b.message; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const { url } = await res.json();
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to download document');
    } finally {
      setDocBusy(null);
    }
  };

  // Generate the акт PDF, store it on the order, open it, then refresh the detail.
  const generateAct = async () => {
    if (!detailId) return;
    setDocBusy('act');
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/act/generate`, { method: 'POST' });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b?.message) msg = b.message; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const { url } = await res.json();
      if (url) window.open(url, '_blank', 'noopener');
      await reloadDetail();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to generate act');
    } finally {
      setDocBusy(null);
    }
  };

  const uploadAct = async (file: File) => {
    if (!detailId) return;
    if (file.size > 15 * 1024 * 1024) {
      alert(t('admin:resultTooLarge'));
      return;
    }
    setActBusy(true);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('read error'));
        reader.readAsDataURL(file);
      });
      const res = await authFetch(`/api/admin/orders/${detailId}/act`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, contentType: file.type, contentBase64: dataUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      await reloadDetail();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to upload act');
    } finally {
      setActBusy(false);
    }
  };

  const downloadAct = async () => {
    if (!detailId) return;
    try {
      const res = await authFetch(`/api/admin/orders/${detailId}/act`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url } = await res.json();
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to download act');
    }
  };

  const BOARD_COLUMNS = [
    { status: 'Pending', cls: 'col-pending', titleKey: 'admin:statusPending' },
    { status: 'SampleReceived', cls: 'col-sample', titleKey: 'admin:statusSampleReceived' },
    { status: 'InProgress', cls: 'col-inprogress', titleKey: 'admin:statusInProgress' },
    { status: 'Completed', cls: 'col-completed', titleKey: 'admin:statusCompleted' },
    { status: 'Cancelled', cls: 'col-cancelled', titleKey: 'admin:statusCancelled' },
  ];

  // Terminal transitions (they also trigger lab notifications) must be confirmed.
  const needsConfirm = (status: string) =>
    ['completed', 'cancelled'].includes(status.toLowerCase());

  // Public order label for an order id, looked up across the board, the table
  // list, and the open detail: the domain reference (WA-/AA-/GA-…), falling back
  // to the internal EM number and then the raw id.
  const orderLabel = (orderId: string): string => {
    const o = Object.values(board)
      .flatMap((c) => c.orders)
      .find((x) => x.orderId === orderId);
    const fromDetail = detail?.orderId === orderId ? detail : undefined;
    return publicRef(o ?? fromDetail) || o?.orderNumber || fromDetail?.orderNumber || orderId;
  };

  const requestStatusChange = (orderId: string, newStatus: string) => {
    if (needsConfirm(newStatus)) {
      setCancelReason('');
      setConfirmChange({ orderId, status: newStatus, label: orderLabel(orderId) });
    } else {
      updateOrderStatus(orderId, newStatus);
    }
  };

  const handleCardDrop = (newStatus: string) => {
    const orderId = draggingId;
    setDraggingId(null);
    setDragOverCol(null);
    if (!orderId) return;
    const order = Object.values(board)
      .flatMap((c) => c.orders)
      .find((o) => o.orderId === orderId);
    if (order && order.status.toLowerCase() !== newStatus.toLowerCase()) {
      requestStatusChange(orderId, newStatus);
    }
  };

  useEffect(() => {
    fetchBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, myOnly, dateFrom, dateTo, assigneeFilter]);

  // Filters applied to every board column. "My orders" takes precedence over
  // the assignee select.
  const appendCommonFilters = (p: URLSearchParams) => {
    if (searchTerm.trim()) p.append('search', searchTerm.trim());
    if (dateFrom) p.append('dateFrom', dateFrom);
    if (dateTo) p.append('dateTo', dateTo);
    const assignee = myOnly && me ? me.userId : assigneeFilter;
    if (assignee) p.append('assigneeId', assignee);
  };

  const anyFilterActive =
    !!searchTerm.trim() || !!dateFrom || !!dateTo || !!assigneeFilter || myOnly;

  const resetFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setAssigneeFilter('');
    setMyOnly(false);
  };

  // Build the query for a single board column (one status).
  const boardColumnQuery = (status: string, page: number) => {
    const p = new URLSearchParams();
    p.append('status', status);
    p.append('page', page.toString());
    p.append('pageSize', BOARD_PAGE_SIZE.toString());
    appendCommonFilters(p);
    return p.toString();
  };

  // Load page 1 of every column in parallel. Each column reports its own true
  // total, so headers show the real count without fetching the whole history.
  const fetchBoard = async () => {
    setLoading(true);
    setError(null);
    try {
      const entries = await Promise.all(
        BOARD_COLUMNS.map(async (col) => {
          const res = await authFetch(`/api/admin/orders?${boardColumnQuery(col.status, 1)}`);
          if (!res.ok) throw new Error(`Failed to fetch orders: ${res.status}`);
          const data: AdminOrdersResponse = await res.json();
          return [col.status, { orders: data.orders, total: data.totalCount, page: 1, loadingMore: false }] as const;
        }),
      );
      setBoard(Object.fromEntries(entries));
    } catch (error) {
      console.error('Error fetching board:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  // Append the next page of a single column on demand ("load more").
  const loadMoreColumn = async (status: string) => {
    const col = board[status];
    if (!col || col.loadingMore || col.orders.length >= col.total) return;
    setBoard((b) => ({ ...b, [status]: { ...b[status], loadingMore: true } }));
    try {
      const nextPage = col.page + 1;
      const res = await authFetch(`/api/admin/orders?${boardColumnQuery(status, nextPage)}`);
      if (!res.ok) throw new Error(`Failed to fetch orders: ${res.status}`);
      const data: AdminOrdersResponse = await res.json();
      setBoard((b) => ({
        ...b,
        [status]: {
          orders: [...b[status].orders, ...data.orders],
          total: data.totalCount,
          page: nextPage,
          loadingMore: false,
        },
      }));
    } catch (error) {
      console.error('Error loading more orders:', error);
      setBoard((b) => ({ ...b, [status]: { ...b[status], loadingMore: false } }));
    }
  };

  const selectedCreatePackage = createPackages.find((p) => p.id === createForm.packageId);

  const openCreate = async () => {
    setCreateForm(emptyCreateForm);
    setShowCreate(true);
    if (createPackages.length === 0) {
      try {
        const res = await authFetch('/api/admin/lab/packages');
        if (res.ok) setCreatePackages(await res.json());
      } catch {
        /* leave the package list empty; user sees no options */
      }
    }
  };

  const createPrice = Number(createForm.price.replace(',', '.')) || 0;

  const submitCreate = async () => {
    if (!createForm.customerName.trim() || !createForm.customerEmail.trim() || !selectedCreatePackage) {
      alert(t('admin:createOrderValidation'));
      return;
    }
    if (createPrice <= 0) {
      alert(t('admin:coPriceRequired'));
      return;
    }
    setCreating(true);
    try {
      const res = await authFetch('/api/admin/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: createForm.customerName.trim(),
          customerEmail: createForm.customerEmail.trim(),
          customerPhone: createForm.customerPhone.trim() || undefined,
          packageId: selectedCreatePackage.id,
          packageName: selectedCreatePackage.name,
          unitAmount: createPrice,
          currency: 'UAH',
          quantity: Number(createForm.quantity) || 1,
          paymentMethod: createForm.paymentMethod,
          isPaid: createForm.isPaid,
          notes: createForm.notes.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      setShowCreate(false);
      fetchBoard();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to create order');
    } finally {
      setCreating(false);
    }
  };

  // Mark an order paid / unpaid. Updates the board and the open detail modal.
  const setPaid = async (orderId: string, isPaid: boolean) => {
    try {
      const res = await authFetch(`/api/admin/orders/${orderId}/payment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPaid }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      setBoard((b) => {
        const next: Record<string, BoardColumn> = {};
        for (const [k, col] of Object.entries(b)) {
          next[k] = { ...col, orders: col.orders.map((o) => (o.orderId === orderId ? { ...o, isPaid } : o)) };
        }
        return next;
      });
      setDetail((d) => (d && d.orderId === orderId ? { ...d, isPaid } : d));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to update payment');
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string, reason?: string) => {
    setIsUpdatingStatus(orderId);

    try {
      const response = await authFetch(`/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, reason: reason?.trim() || undefined }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update order status: ${response.status}`);
      }

      // Refresh the board so the card moves columns and the counts update.
      fetchBoard();
      // Keep the open detail modal (status badge + history) in sync.
      if (detailId === orderId) reloadDetail();
    } catch (error) {
      console.error('Error updating order status:', error);
      alert(`${t('admin:errorUpdatingStatus')} ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUpdatingStatus(null);
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return t('admin:statusPending');
      case 'samplereceived':
        return t('admin:statusSampleReceived');
      case 'inprogress':
        return t('admin:statusInProgress');
      case 'completed':
        return t('admin:statusCompleted');
      case 'cancelled':
        return t('admin:statusCancelled');
      default:
        return status;
    }
  };

  // For total amounts - convert EUR to UAH
  const EUR_TO_UAH_RATE = 42.5;
  const formatMoney = (amount: number, currency: string) => {
    const uahAmount = currency === 'EUR' ? amount * EUR_TO_UAH_RATE : amount;

    try {
      return (
        new Intl.NumberFormat('uk-UA', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(uahAmount) + ' грн'
      );
    } catch (error) {
      return `${uahAmount.toFixed(2)} грн`;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('uk-UA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return dateString;
    }
  };

  // Compact age (since the order was placed) for the board cards.
  const orderAge = (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime();
    const days = Math.floor(ms / 86400000);
    if (days >= 1) return `${days}${t('admin:ageDayShort')}`;
    const hrs = Math.floor(ms / 3600000);
    if (hrs >= 1) return `${hrs}${t('admin:ageHourShort')}`;
    return t('admin:ageNow');
  };

  // The customer-facing landing reference (WA-…) parsed from the order notes —
  // this is the number the customer sees in emails / on the tracking page.
  const landingRef = (notes?: string | null): string | null => {
    if (!notes) return null;
    const m = notes.match(/Заявка з лендингу:\s*(\S+)/);
    return m ? m[1] : null;
  };

  // Compact any reference to {PREFIX}-{last segment}: new refs (WA-9ECD5B) are
  // already compact and pass through; legacy refs (WA-20260621155308-A2E025) are
  // shortened to WA-A2E025. Prefix-agnostic so AA-/GA- compact too.
  const compactRef = (ref?: string | null): string | null => {
    if (!ref) return null;
    const parts = ref.split('-');
    return parts.length >= 3 ? `${parts[0]}-${parts[parts.length - 1]}` : ref;
  };

  // The public, domain-prefixed reference (WA-/AA-/GA-…) shown as the order's
  // headline. Uses the first-class field, falling back to the WA- parsed from
  // notes for legacy landing orders created before the field existed.
  const publicRef = (o?: { reference?: string | null; notes?: string | null } | null): string | null =>
    compactRef(o?.reference || landingRef(o?.notes));

  const formatBytes = (n: number) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  // Landing checkout writes a "Label: value" notes blob whose fields we now show
  // structurally (customer, sample params, B2B requisites). Strip those known
  // lines so the free-text "Нотатки" card only shows genuinely extra content
  // (e.g. from manually-created orders) instead of duplicating the sections.
  const residualNotes = (notes?: string | null): string => {
    if (!notes) return '';
    const known = /^(B2B заявка|Режим ПДВ|Контакт|Джерело|Мета|Місце|Дата відбору|Оплата)\s*:/;
    return notes
      .split('\n')
      .filter((line) => line.trim() && !known.test(line.trim()))
      .join('\n')
      .trim();
  };

  // Human-readable one-liner for an audit event.
  const describeEvent = (e: OrderEvent): string => {
    switch (e.type) {
      case 'Created':
        return t('admin:histCreated');
      case 'Status':
        return t('admin:histStatus', {
          from: getStatusDisplayName(e.from || ''),
          to: getStatusDisplayName(e.to || ''),
        });
      case 'Assignment':
        return e.to
          ? t('admin:histAssigned', { name: e.to })
          : t('admin:histUnassigned');
      case 'Payment':
        return t('admin:histPayment', { to: e.to || '' });
      case 'Result':
        return t('admin:histResult', { name: e.to || '' });
      default:
        return e.type;
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const boardTotal = BOARD_COLUMNS.reduce((sum, col) => sum + (board[col.status]?.total ?? 0), 0);

  if (loading && Object.keys(board).length === 0) {
    return (
      <div className="admin-page admin-page--fluid">
        <div className="admin-header">
          <h1>{t('admin:manageOrders')}</h1>
        </div>
        <div className="loading">{t('admin:loadingOrders')}</div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-page--fluid">
      <div className="admin-header">
        <h1>
          {t('admin:manageOrders')}
          <span className="h1-count" title={t('admin:ordersFound')}>{boardTotal}</span>
        </h1>
        <div className="orders-header-actions">
          <button type="button" className="btn-add-order" onClick={openCreate}>
            + {t('admin:addOrder')}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Filters and Search */}
      <button
        type="button"
        className="admin-filters-toggle"
        onClick={() => setFiltersOpen((v) => !v)}
      >
        {t('admin:filtersToggle')}
        {anyFilterActive && <span className="admin-filters-dot" />}
        <span className="admin-filters-chevron">{filtersOpen ? '▴' : '▾'}</span>
      </button>
      <div className={`orders-toolbar${filtersOpen ? ' is-open' : ''}`}>
        <div className="ot-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            id="search"
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('admin:searchOrdersPlaceholder')}
            aria-label={t('admin:searchOrders')}
            className="search-input"
          />
        </div>

        <div className="ot-dates" title={t('admin:filterPeriod')}>
          <input
            id="date-from"
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-label={t('admin:filterDateFrom')}
            className="status-filter"
          />
          <span className="ot-dates-sep">—</span>
          <input
            id="date-to"
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            aria-label={t('admin:filterDateTo')}
            className="status-filter"
          />
        </div>

        {me?.isAdmin && (
          <select
            id="assignee-filter"
            value={assigneeFilter}
            disabled={myOnly}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            aria-label={t('admin:filterByAssignee')}
            className="status-filter ot-assignee"
          >
            <option value="">{t('admin:allAssignees')}</option>
            <option value="none">{t('admin:unassigned')}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {userDisplay(u)}
              </option>
            ))}
          </select>
        )}

        {me && (
          <label className={`ot-chip${myOnly ? ' is-active' : ''}`}>
            <input id="my-only" type="checkbox" checked={myOnly} onChange={(e) => setMyOnly(e.target.checked)} />
            {t('admin:myOrders')}
          </label>
        )}

        {anyFilterActive && (
          <button type="button" className="ot-reset" onClick={resetFilters}>
            ✕ {t('admin:resetFilters')}
          </button>
        )}
      </div>

      <div className="kanban">
          {BOARD_COLUMNS.map((col) => {
            const colState = board[col.status];
            const colOrders = colState?.orders ?? [];
            const colTotal = colState?.total ?? 0;
            const hasMore = colOrders.length < colTotal;
            return (
              <div
                key={col.status}
                className={`kanban-col${dragOverCol === col.status ? ' drag-over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOverCol !== col.status) setDragOverCol(col.status);
                }}
                onDragLeave={() => setDragOverCol((c) => (c === col.status ? null : c))}
                onDrop={() => handleCardDrop(col.status)}
              >
                <div className="kanban-col-header">
                  <span>{t(col.titleKey)}</span>
                  <span className="kanban-count">{colTotal}</span>
                </div>
                <div className="kanban-col-body">
                  {(() => {
                    const renderCard = (order: AdminOrder) => {
                      const active = col.status !== 'Completed' && col.status !== 'Cancelled';
                      const ageDays = Math.floor((Date.now() - new Date(order.orderDate).getTime()) / 86400000);
                      const ageCls = active ? (ageDays >= 7 ? ' kc-age-red' : ageDays >= 3 ? ' kc-age-amber' : '') : '';
                      return (
                        <div
                          key={order.orderId}
                          className={`kanban-card ${col.cls}${draggingId === order.orderId ? ' dragging' : ''}`}
                          draggable={isUpdatingStatus === null}
                          onDragStart={() => setDraggingId(order.orderId)}
                          onDragEnd={() => {
                            setDraggingId(null);
                            setDragOverCol(null);
                          }}
                          onClick={() => openDetail(order.orderId)}
                          title={t('admin:openDetails')}
                        >
                          <div className="kc-head">
                            <span className="kc-package">{publicRef(order) || order.orderNumber || order.packageName || order.customer.name}</span>
                            <span className={`kc-paid ${order.isPaid ? 'is-paid' : 'is-unpaid'}`}>
                              {order.isPaid ? t('admin:paid') : t('admin:unpaid')}
                            </span>
                            {order.hasResult && (
                              <span className="kc-result" title={t('admin:resultAttachedHint')}>📄</span>
                            )}
                          </div>
                          {order.orderNumber && publicRef(order) && (
                            <span className="kc-emnum" title={t('admin:internalNumber')}>{order.orderNumber}</span>
                          )}
                          {order.packageName && (
                            <div className="kc-subtitle">{order.packageName}</div>
                          )}
                          {(order.sampleLocation || order.sampleDate) && (
                            <div className="kc-meta">
                              {order.sampleLocation && <span className="kc-meta-item">📍 {order.sampleLocation}</span>}
                              {order.sampleDate && <span className="kc-meta-item">🧪 {order.sampleDate}</span>}
                            </div>
                          )}
                          <div className="kc-customer">{order.customer.name}</div>
                          <div className="kc-footer">
                            {order.assigneeName ? (
                              <span className="kc-assignee" title={order.assigneeName}>
                                {order.assigneeAvatarUrl ? (
                                  <img className="assignee-avatar" src={order.assigneeAvatarUrl} alt="" />
                                ) : (
                                  <span className="assignee-avatar">{order.assigneeName.charAt(0).toUpperCase()}</span>
                                )}
                                <span className="kc-assignee-name">{order.assigneeName}</span>
                              </span>
                            ) : (
                              <span className="kc-unassigned">{t('admin:unassigned')}</span>
                            )}
                            <span className="kc-grow" />
                            <span className={`kc-age${ageCls}`}>⏱ {orderAge(order.orderDate)}</span>
                          </div>
                        </div>
                      );
                    };

                    // The Completed column keeps the last 7 days in view and tucks
                    // older items behind a toggle so the active pipeline stays clean.
                    const isCompleted = col.status === 'Completed';
                    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
                    const dateOf = (o: AdminOrder) => new Date(o.updatedAt || o.orderDate).getTime();
                    const recent = isCompleted ? colOrders.filter((o) => dateOf(o) >= cutoff) : colOrders;
                    const older = isCompleted ? colOrders.filter((o) => dateOf(o) < cutoff) : [];

                    return (
                      <>
                        {recent.map(renderCard)}

                        {isCompleted && older.length > 0 && !showOlderCompleted && (
                          <button
                            type="button"
                            className="kanban-older-toggle"
                            onClick={() => setShowOlderCompleted(true)}
                          >
                            {t('admin:boardShowEarlier', { count: older.length })}
                          </button>
                        )}
                        {isCompleted && older.length > 0 && showOlderCompleted && (
                          <>
                            <div className="kanban-section-label">{t('admin:boardEarlier')}</div>
                            {older.map(renderCard)}
                            <button
                              type="button"
                              className="kanban-older-toggle"
                              onClick={() => setShowOlderCompleted(false)}
                            >
                              {t('admin:boardCollapse')}
                            </button>
                          </>
                        )}

                        {colOrders.length === 0 && <div className="kanban-empty">{t('admin:boardEmpty')}</div>}

                        {hasMore && (
                          <button
                            type="button"
                            className="kanban-load-more"
                            onClick={() => loadMoreColumn(col.status)}
                            disabled={colState?.loadingMore}
                          >
                            {colState?.loadingMore
                              ? t('admin:loading')
                              : `${t('admin:loadMore')} (${colTotal - colOrders.length})`}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>

      {loading && Object.keys(board).length > 0 && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {confirmChange && (
        // z above the detail modal (z-9990): a second .modal-overlay rule (z-index:1000)
        // from another stylesheet otherwise wins and puts this behind the detail modal.
        <div className="modal-overlay" style={{ zIndex: 10000 }} onClick={() => setConfirmChange(null)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin:confirmChangeTitle')}</h2>
              <button className="modal-close" onClick={() => setConfirmChange(null)}>
                ×
              </button>
            </div>
            <div className="confirm-body">
              <p>
                {t('admin:confirmChangeQuestion', {
                  id: confirmChange.label,
                  status: getStatusDisplayName(confirmChange.status),
                })}
              </p>
              {confirmChange.status.toLowerCase() === 'completed' &&
                detail?.orderId === confirmChange.orderId &&
                !detail?.result && (
                  <div
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.6rem 0.8rem',
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderRadius: '8px',
                      color: '#92400e',
                      fontSize: '0.88rem',
                      lineHeight: 1.4,
                    }}
                  >
                    ⚠️ {t('admin:completeNoResultWarning')}
                  </div>
                )}
              {confirmChange.status.toLowerCase() === 'cancelled' && (
                <div className="form-group" style={{ marginTop: '0.75rem' }}>
                  <label htmlFor="cancel-reason">{t('admin:cancelReasonLabel')}</label>
                  <textarea
                    id="cancel-reason"
                    rows={3}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder={t('admin:cancelReasonPlaceholder')}
                    className="search-input"
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
              )}
            </div>
            <div className="confirm-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmChange(null)}>
                {t('admin:confirmNo')}
              </button>
              <button
                className={`btn ${confirmChange.status.toLowerCase() === 'cancelled' ? 'btn-danger' : 'btn-success'}`}
                onClick={() => {
                  updateOrderStatus(
                    confirmChange.orderId,
                    confirmChange.status,
                    confirmChange.status.toLowerCase() === 'cancelled' ? cancelReason : undefined,
                  );
                  setConfirmChange(null);
                }}
              >
                {t('admin:confirmYes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailId && (
        <div
          className="fixed inset-0 z-[9990] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm"
          onClick={closeDetail}
        >
          <div
            className="od-modern relative my-4 w-full max-w-4xl rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-3">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate text-lg font-bold text-slate-900">{publicRef(detail) || detail?.orderNumber || detailId}</h2>
                    {detail?.orderNumber && (
                      <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{detail.orderNumber}</span>
                    )}
                  </div>
                </div>
                <button
                  className="shrink-0 rounded-lg p-1 text-2xl leading-none text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                  onClick={closeDetail}
                >
                  ×
                </button>
              </div>

              {detail && (
                <div className="mt-3 flex gap-1 border-b border-slate-200">
                  {(
                    [
                      ['overview', 'detTabOverview'],
                      ['payment', 'detTabPayment'],
                      ['analyses', 'detTabAnalyses'],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setDetailTab(key)}
                      className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-1 text-sm font-medium transition ${detailTab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                    >
                      {t(`admin:${label}`)}
                      {key === 'analyses' && measurements && (
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${measurements.complete ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-50 text-blue-600'}`}>
                          {measurements.filled}/{measurements.total}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {detailLoading || !detail ? (
              <div className="p-10 text-center text-sm text-slate-400">
                {detailLoading ? t('admin:loadingOrders') : t('admin:leadsFetchError')}
              </div>
            ) : (
              <div className={`grid grid-cols-1 gap-3 rounded-b-2xl bg-slate-50 p-4${detailTab === 'overview' ? ' lg:grid-cols-[minmax(0,1fr)_272px]' : ''}`}>
                {/* MAIN COLUMN — tab content */}
                <div className="flex min-w-0 flex-col gap-3">

                  {detailTab === 'overview' && (
                    <>
                      <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:detSectionCustomer')}</div>
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-sm font-semibold text-indigo-500">
                            {(detail.customer.name || '?').charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-900">{detail.customer.name}</div>
                            <div className="truncate text-sm text-slate-500">{detail.customer.email}</div>
                            {detail.customer.phone && <div className="text-sm text-slate-500">{detail.customer.phone}</div>}
                          </div>
                        </div>
                        {detail.b2b && (detail.b2b.legalName || detail.b2b.contactPerson) && (
                          <div className="mt-2 flex flex-col gap-1 border-t border-slate-100 pt-2 text-sm text-slate-600">
                            {detail.b2b.legalName && <div><span className="text-slate-400">{t('admin:b2bLegalName')}: </span><span className="font-medium text-slate-800">{detail.b2b.legalName}</span></div>}
                            {detail.b2b.contactPerson && <div><span className="text-slate-400">{t('admin:b2bContact')}: </span>{detail.b2b.contactPerson}</div>}
                          </div>
                        )}
                      </div>

                      {(detail.sampleSource || detail.sampleGoal || detail.sampleLocation || detail.sampleDate) && (
                        <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:detSectionSample')}</div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {detail.sampleSource && (
                              <div><div className="text-xs text-slate-400">{t('admin:detSampleSource')}</div><div className="text-sm text-slate-800">{detail.sampleSource}</div></div>
                            )}
                            {detail.sampleGoal && (
                              <div><div className="text-xs text-slate-400">{t('admin:detSampleGoal')}</div><div className="text-sm text-slate-800">{detail.sampleGoal}</div></div>
                            )}
                            {detail.sampleLocation && (
                              <div><div className="text-xs text-slate-400">{t('admin:detSampleLocation')}</div><div className="text-sm text-slate-800">{detail.sampleLocation}</div></div>
                            )}
                            {detail.sampleDate && (
                              <div><div className="text-xs text-slate-400">{t('admin:detSampleDate')}</div><div className="text-sm text-slate-800">{detail.sampleDate}</div></div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:detSectionOrder')}</div>
                        <ul className="flex flex-col gap-3 text-sm">
                          {detail.items.map((it) => {
                            const groups = it.indicators || [];
                            const count = groups.reduce((n, g) => n + g.items.length, 0);
                            const open = !!openIndicators[it.lineId];
                            return (
                              <li key={it.lineId} className="flex flex-col gap-1">
                                <div className="flex items-baseline justify-between gap-3">
                                  <span className="min-w-0">
                                    <span className="font-medium text-slate-800">{it.packageName}</span>
                                    {it.selectedOptions?.length > 0 && <span className="text-xs text-slate-400"> · {it.selectedOptions.join(', ')}</span>}
                                    {it.specialInstructions && <div className="text-xs text-slate-400">{it.specialInstructions}</div>}
                                  </span>
                                  {it.quantity > 1 && <span className="shrink-0 text-slate-500">×{it.quantity}</span>}
                                </div>
                                {count > 0 && (
                                  <>
                                    <button
                                      type="button"
                                      className="self-start text-xs font-medium text-blue-600 hover:underline"
                                      onClick={() => setOpenIndicators((s) => ({ ...s, [it.lineId]: !s[it.lineId] }))}
                                    >
                                      {open ? t('admin:detIndicatorsHide') : t('admin:detIndicatorsShow', { count })}
                                    </button>
                                    {open && (
                                      <div className="mt-1 flex flex-col gap-2 rounded-lg bg-slate-50 p-3">
                                        {groups.map((g) => (
                                          <div key={g.group}>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{g.group}</div>
                                            <div className="text-sm text-slate-700">{g.items.join(', ')}</div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>

                      <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:resultTitle')}</div>
                        {detail.result ? (
                          <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-2.5">
                            <div className="flex min-w-0 items-center gap-3">
                              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-base ring-1 ring-slate-200">📄</span>
                              <div className="min-w-0">
                                <button type="button" className="block max-w-full truncate text-left text-sm font-semibold text-blue-600 hover:underline" onClick={downloadResult}>
                                  {detail.result.fileName}
                                </button>
                                <div className="text-xs text-slate-400">
                                  {formatBytes(detail.result.size)} · {formatDate(detail.result.uploadedAt)} · {detail.result.uploadedByName}
                                </div>
                              </div>
                            </div>
                            <span className="flex shrink-0 items-center gap-1.5">
                              <button type="button" className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50" onClick={downloadResult}>
                                {t('admin:resultDownload')}
                              </button>
                              <label className={`cursor-pointer rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 ${resultBusy ? 'cursor-default opacity-60' : ''}`}>
                                {resultBusy ? t('admin:resultUploading') : t('admin:resultReplace')}
                                <input
                                  type="file"
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                  className="hidden"
                                  disabled={resultBusy}
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) uploadResult(f);
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            </span>
                          </div>
                        ) : (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm text-slate-400">{t('admin:resultNone')}</span>
                        <label className={`inline-flex cursor-pointer items-center rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 ${resultBusy ? 'cursor-default opacity-60' : ''}`}>
                          {resultBusy ? t('admin:resultUploading') : t('admin:resultUpload')}
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                            className="hidden"
                            disabled={resultBusy}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadResult(f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        </div>
                        )}
                      </div>

                      {detail.b2b && (
                        <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:actTitle')}</div>
                          {detail.act ? (
                            <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-2.5">
                              <div className="flex min-w-0 items-center gap-3">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-base ring-1 ring-slate-200">📃</span>
                                <div className="min-w-0">
                                  <button type="button" className="block max-w-full truncate text-left text-sm font-semibold text-blue-600 hover:underline" onClick={downloadAct}>
                                    {detail.act.fileName}
                                  </button>
                                  <div className="text-xs text-slate-400">
                                    {formatDate(detail.act.createdAt)} · {detail.act.byName} · {detail.act.source === 'uploaded' ? t('admin:actSourceUploaded') : t('admin:actSourceGenerated')}
                                  </div>
                                </div>
                              </div>
                              <button type="button" className="shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50" onClick={downloadAct}>
                                {t('admin:resultDownload')}
                              </button>
                            </div>
                          ) : null}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {!detail.act && <span className="mr-auto text-sm text-slate-400">{t('admin:actNone')}</span>}
                            <button type="button" disabled={docBusy === 'act'} className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50" onClick={generateAct}>
                              {docBusy === 'act' ? t('admin:b2bGenerating') : (detail.act ? `📃 ${t('admin:actRegenerate')}` : `📃 ${t('admin:actGenerate')}`)}
                            </button>
                            <label className={`inline-flex cursor-pointer items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 ${actBusy ? 'cursor-default opacity-60' : ''}`}>
                              {actBusy ? t('admin:resultUploading') : t('admin:actUpload')}
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                className="hidden"
                                disabled={actBusy}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) uploadAct(f);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          </div>
                        </div>
                      )}

                      <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:commentsTitle')}</div>
                        <div className={detail.comments && detail.comments.length > 0 ? 'mb-2 flex flex-col gap-1.5' : 'hidden'}>
                          {detail.comments && detail.comments.length > 0 ? (
                            detail.comments.map((c) => (
                              <div key={c.id} className="rounded-lg bg-slate-50 p-2.5">
                                <div className="mb-1 flex items-center justify-between gap-2">
                                  <span className="text-sm font-semibold text-slate-800">{c.authorName}</span>
                                  <span className="text-xs text-slate-400">{formatDate(c.at)}</span>
                                </div>
                                <div className="whitespace-pre-wrap break-words text-sm text-slate-700">{c.text}</div>
                              </div>
                            ))
                          ) : null}
                        </div>
                        <div className="flex items-end gap-2">
                          <textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder={t('admin:commentsPlaceholder')}
                            rows={1}
                            className="min-h-9 w-full flex-1 resize-y rounded-lg border border-slate-200 px-2.5 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                            disabled={commentBusy || !commentText.trim()}
                            onClick={submitComment}
                          >
                            {commentBusy ? t('admin:commentsSending') : t('admin:commentsAdd')}
                          </button>
                        </div>
                      </div>

                      {detail.history && detail.history.length > 0 && (
                        <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:historyTitle')}</div>
                          <ul className="flex flex-col gap-2">
                            {[...detail.history].reverse().map((e, i) => (
                              <li key={i} className="flex gap-3">
                                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500 ring-4 ring-blue-50" />
                                <div className="min-w-0">
                                  <div className="text-sm text-slate-800">{describeEvent(e)}</div>
                                  <div className="text-xs text-slate-400">
                                    {formatDate(e.at)} · {e.actorName}
                                    {e.note ? ` · ${e.note}` : ''}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {residualNotes(detail.notes) && (
                        <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:detSectionNotes')}</div>
                          <pre className="whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 font-sans text-sm text-slate-700">{residualNotes(detail.notes)}</pre>
                        </div>
                      )}
                    </>
                  )}

                  {detailTab === 'analyses' && (
                    <>
                      {!measurements ? (
                        <div className="rounded-xl border border-slate-100 bg-white p-6 text-center shadow-sm">
                          {detail.items.some((it) => (it.indicators || []).length > 0) ? (
                            <>
                              <p className="mb-4 text-sm text-slate-500">{t('admin:measCreateHint')}</p>
                              <button
                                type="button"
                                disabled={measCreating}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                                onClick={createMeasurementSheet}
                              >
                                {measCreating ? t('admin:measCreating') : `🧪 ${t('admin:measCreate')}`}
                              </button>
                            </>
                          ) : (
                            <p className="text-sm text-slate-400">{t('admin:measNoComposition')}</p>
                          )}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                          <div className="mb-4 flex items-center gap-3">
                            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                              <div
                                className={`h-full rounded-full transition-all ${measurements.complete ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                style={{ width: `${measurements.total ? Math.round((measurements.filled / measurements.total) * 100) : 0}%` }}
                              />
                            </div>
                            <span className="shrink-0 text-xs text-slate-500">
                              {t('admin:measProgress', { filled: measurements.filled, total: measurements.total })}
                            </span>
                          </div>

                          <div className="mb-3 flex flex-wrap gap-1.5">
                            {measurements.groups.map((g) => {
                              const filled = g.rows.filter((r) => r.value).length;
                              const active = (measGroup || measurements.groups[0]?.group) === g.group;
                              const done = filled === g.rows.length;
                              return (
                                <button
                                  key={g.group}
                                  type="button"
                                  onClick={() => setMeasGroup(g.group)}
                                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${active ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-500 hover:text-slate-700'}`}
                                >
                                  {g.group}
                                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${done ? 'bg-emerald-100 text-emerald-700' : active ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                                    {filled}/{g.rows.length}
                                  </span>
                                </button>
                              );
                            })}
                          </div>

                          <div className="hidden grid-cols-[minmax(0,1fr)_64px_80px_100px_132px] gap-2 px-2 pb-1 text-xs uppercase tracking-wide text-slate-400 sm:grid">
                            <span>{t('admin:measColIndicator')}</span>
                            <span>{t('admin:measColUnit')}</span>
                            <span>{t('admin:measColNorm')}</span>
                            <span>{t('admin:measColValue')}</span>
                            <span className="text-center">{t('admin:measColVerdict')}</span>
                          </div>

                          {measurements.groups
                            .filter((g) => g.group === (measGroup || measurements.groups[0]?.group))
                            .map((g) => (
                            <div key={g.group} className="mb-1">
                              {g.rows.map((r) => (
                                <div
                                  key={r.code}
                                  className="grid grid-cols-2 items-center gap-2 border-t border-slate-100 px-2 py-1 sm:grid-cols-[minmax(0,1fr)_64px_80px_100px_132px]"
                                >
                                  <div className="col-span-2 min-w-0 sm:col-span-1">
                                    <div className="break-words text-sm text-slate-800">{r.name}</div>
                                    {r.methods && r.methods.length > 1 ? (
                                      <select
                                        value={r.methodId || 'default'}
                                        disabled={measSavingCode === r.code}
                                        onChange={(e) => changeMethod(r, e.target.value)}
                                        className="mt-0.5 max-w-full rounded border border-slate-200 bg-white px-1 py-0.5 text-xs text-slate-500 outline-none focus:border-blue-500 disabled:opacity-50"
                                        title={r.method}
                                      >
                                        {r.methods.map((m) => (
                                          <option key={m.id} value={m.id}>
                                            {(m.name || m.ndd || t('admin:invMethodDefault')) + (m.ndd && m.name ? ` — ${m.ndd}` : '')}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      r.method && <div className="truncate text-xs text-slate-400">{r.method}</div>
                                    )}
                                  </div>
                                  <span className="hidden text-xs text-slate-500 sm:block">{r.unit || '—'}</span>
                                  <span className="hidden text-xs text-slate-500 sm:block">{r.normText || '—'}</span>
                                  <input
                                    type="text"
                                    value={measDrafts[r.code] ?? ''}
                                    placeholder={t('admin:measValuePlaceholder')}
                                    disabled={measSavingCode === r.code}
                                    title={r.measuredByName ? `${r.measuredByName} · ${formatDate(r.measuredAt || '')}` : undefined}
                                    className={`w-full rounded-lg border px-2 py-1.5 text-sm outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 ${r.verdict === 'out' ? 'border-rose-300' : 'border-slate-200'}`}
                                    onChange={(e) => setMeasDrafts((s) => ({ ...s, [r.code]: e.target.value }))}
                                    onBlur={() => saveMeasurement(r)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                    }}
                                  />
                                  <span className="min-w-0 text-center">
                                    {measSavingCode === r.code ? (
                                      <span className="text-xs text-slate-400">…</span>
                                    ) : r.verdict === 'ok' ? (
                                      <span className="inline-block rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">{t('admin:measOk')}</span>
                                    ) : r.verdict === 'out' ? (
                                      <span className="inline-block rounded-md bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">{t('admin:measOut')}</span>
                                    ) : (
                                      <span className="text-xs text-slate-300">—</span>
                                    )}
                                    {r.measuredByName && (
                                      <span
                                        className="mt-0.5 block truncate text-xs text-slate-400"
                                        title={`${r.measuredByName}${r.measuredAt ? ' · ' + formatDate(r.measuredAt) : ''}`}
                                      >
                                        {r.measuredByName.split('@')[0]}
                                      </span>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}

                          {measurements && measurements.complete && (
                            <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/60 p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="text-sm text-emerald-800">
                                  ✓ {t('admin:measComplete')}
                                  {measurements.protocol && (
                                    <div className="mt-0.5 text-xs text-emerald-700/80">
                                      {t('admin:protoGenerated', {
                                        number: measurements.protocol.number,
                                        date: formatDate(measurements.protocol.generatedAt),
                                        by: measurements.protocol.generatedByName,
                                      })}
                                    </div>
                                  )}
                                </div>
                                {!protoOpen && (
                                  <button
                                    type="button"
                                    className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
                                    onClick={openProtoForm}
                                  >
                                    📋 {measurements.protocol ? t('admin:protoRegenerate') : t('admin:protoGenerate')}
                                  </button>
                                )}
                              </div>

                              {protoOpen && (
                                <div className="mt-3 grid grid-cols-1 gap-3 border-t border-emerald-100 pt-3 sm:grid-cols-2">
                                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                                    {t('admin:protoNumber')}
                                    <input type="text" className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-500" placeholder={t('admin:protoNumberAuto')}
                                      value={protoForm.number} onChange={(e) => setProtoForm((f) => ({ ...f, number: e.target.value }))} />
                                  </label>
                                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                                    {t('admin:protoDate')}
                                    <input type="date" className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-500"
                                      value={protoForm.date} onChange={(e) => setProtoForm((f) => ({ ...f, date: e.target.value }))} />
                                  </label>
                                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                                    {t('admin:protoPerformer')}
                                    <select className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-500"
                                      value={protoForm.performerName}
                                      onChange={(e) => {
                                        const name = e.target.value;
                                        const person = staff.find((s) => s.name === name);
                                        setProtoForm((f) => ({ ...f, performerName: name, performerPosition: person?.position || f.performerPosition }));
                                      }}>
                                      <option value="">{t('admin:protoPick')}</option>
                                      {protoForm.performerName && !staff.some((s) => s.name === protoForm.performerName) && (
                                        <option value={protoForm.performerName}>{protoForm.performerName}</option>
                                      )}
                                      {staff.map((s) => (
                                        <option key={s.id} value={s.name}>{s.name}{s.position ? ` — ${s.position}` : ''}</option>
                                      ))}
                                    </select>
                                  </label>
                                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                                    {t('admin:protoPosition')}
                                    <input type="text" className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-500"
                                      value={protoForm.performerPosition} onChange={(e) => setProtoForm((f) => ({ ...f, performerPosition: e.target.value }))} />
                                  </label>
                                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                                    {t('admin:protoHead')}
                                    <input type="text" list="proto-staff-names" className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-500"
                                      value={protoForm.headName} onChange={(e) => setProtoForm((f) => ({ ...f, headName: e.target.value }))} />
                                    <datalist id="proto-staff-names">
                                      {staff.map((s) => (
                                        <option key={s.id} value={s.name} />
                                      ))}
                                    </datalist>
                                  </label>
                                  <label className="flex flex-col gap-1 text-xs text-slate-500">
                                    {t('admin:protoConclusion')}
                                    <select className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-500"
                                      value={protoForm.complies} onChange={(e) => setProtoForm((f) => ({ ...f, complies: e.target.value }))}>
                                      <option value="auto">{t('admin:protoAuto')}</option>
                                      <option value="yes">{t('admin:protoComplies')}</option>
                                      <option value="no">{t('admin:protoNotComplies')}</option>
                                    </select>
                                  </label>
                                  <div className="flex items-end gap-2 sm:col-span-2">
                                    <button type="button" disabled={protoBusy}
                                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                                      onClick={generateProtocol}>
                                      {protoBusy ? t('admin:protoBusy') : `📋 ${t('admin:protoGenerate')}`}
                                    </button>
                                    <button type="button" disabled={protoBusy}
                                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                                      onClick={() => setProtoOpen(false)}>
                                      {t('admin:cancel')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                    </>
                  )}

                  {detailTab === 'payment' && (
                    <>
                      <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                        <div className="mb-3 flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:detSectionItems')}</span>
                          {detail.b2b && (
                            <span className={`rounded px-2 py-0.5 text-xs font-medium ${detail.b2b.vatMode === 'vat' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                              {detail.b2b.vatMode === 'vat' ? t('admin:b2bVat', { rate: detail.b2b.vatRate }) : t('admin:b2bNoVat')}
                            </span>
                          )}
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                              <th className="pb-2 font-medium">{t('admin:leadsColPackage')}</th>
                              <th className="pb-2 font-medium">{t('admin:detColQty')}</th>
                              <th className="pb-2 font-medium">{t('admin:detColUnit')}</th>
                              <th className="pb-2 text-right font-medium">{t('admin:detColTotal')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.items.map((it) => (
                              <tr key={it.lineId} className="border-t border-slate-100 align-top">
                                <td className="py-2 pr-2">
                                  <div className="font-medium text-slate-800">{it.packageName}</div>
                                  {it.selectedOptions?.length > 0 && <div className="text-xs text-slate-400">{it.selectedOptions.join(', ')}</div>}
                                </td>
                                <td className="py-2 text-slate-600">{it.quantity}</td>
                                <td className="py-2 text-slate-600">{formatMoney(it.unitPrice.amount, it.unitPrice.currency)}</td>
                                <td className="py-2 text-right font-medium text-slate-800">{formatMoney(it.totalPrice.amount, it.totalPrice.currency)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="mt-3 flex flex-col gap-1 border-t border-dashed border-slate-200 pt-3 text-sm">
                          {detail.pricing.discountAmount.amount > 0 && (
                            <>
                              <div className="flex justify-between text-slate-600"><span>{t('admin:detPriceOriginal')}</span><span>{formatMoney(detail.pricing.originalPrice.amount, detail.pricing.originalPrice.currency)}</span></div>
                              <div className="flex justify-between text-slate-600"><span>{t('admin:detPriceDiscount')}</span><span className="text-red-600">−{formatMoney(detail.pricing.discountAmount.amount, detail.pricing.discountAmount.currency)}</span></div>
                            </>
                          )}
                          {detail.appliedDiscountCodes?.length > 0 && (
                            <div className="flex justify-between text-slate-600"><span>{t('admin:detDiscountCodes')}</span><span>{detail.appliedDiscountCodes.join(', ')}</span></div>
                          )}
                          {detail.b2b?.vatMode === 'vat' && (
                            <>
                              <div className="flex justify-between text-slate-600"><span>{t('admin:b2bNet')}</span><span>{formatMoney(detail.pricing.finalPrice.amount, detail.pricing.finalPrice.currency)}</span></div>
                              <div className="flex justify-between text-slate-600"><span>{t('admin:b2bVatRow', { rate: detail.b2b.vatRate })}</span><span>{formatMoney(detail.b2b.vatAmount.amount, detail.b2b.vatAmount.currency)}</span></div>
                            </>
                          )}
                          <div className="mt-1 flex items-baseline justify-between border-t border-dashed border-slate-200 pt-2">
                            <span className="text-xs uppercase tracking-wide text-slate-500">{t('admin:detAmountDue')}</span>
                            <span className="text-lg font-bold text-slate-900">{formatMoney(detail.amountDue.amount, detail.amountDue.currency)}</span>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3 text-sm">
                          <span className="text-slate-500">{t('admin:columnPaymentMethod')}</span>
                          <span className="font-medium text-slate-800">
                            {detail.paymentMethod === 'invoice' ? t('admin:paymentMethodInvoice') : t('admin:paymentMethodCard')}
                          </span>
                        </div>
                      </div>

                      {detail.b2b && (
                        <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:b2bTitle')}</div>
                          <div className="flex flex-col gap-1 text-sm text-slate-600">
                            <div><span className="text-slate-400">{t('admin:b2bLegalName')}: </span><span className="font-medium text-slate-800">{detail.b2b.legalName || '—'}</span></div>
                            <div><span className="text-slate-400">{t('admin:b2bEdrpou')}: </span>{detail.b2b.edrpou || '—'}</div>
                            {detail.b2b.ipnVat && <div><span className="text-slate-400">{t('admin:b2bIpn')}: </span>{detail.b2b.ipnVat}</div>}
                            {detail.b2b.legalAddress && <div><span className="text-slate-400">{t('admin:b2bAddress')}: </span>{detail.b2b.legalAddress}</div>}
                            {detail.b2b.contactPerson && <div><span className="text-slate-400">{t('admin:b2bContact')}: </span>{detail.b2b.contactPerson}</div>}
                          </div>
                        </div>
                      )}

                      {detail.b2b && (
                        <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:b2bInvoiceTitle')}</div>
                          <button type="button" disabled={docBusy === 'invoice'} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50" onClick={() => downloadDoc('invoice')}>
                            {docBusy === 'invoice' ? t('admin:b2bGenerating') : `📄 ${t('admin:b2bDownloadInvoice')}`}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* SIDEBAR — only on the main tab; analyses/payment need the full width. */}
                {detailTab === 'overview' && (
                <aside className="flex flex-col gap-3">
                  <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:assignee')}</div>
                    {(() => {
                      const o = Object.values(board)
                        .flatMap((c) => c.orders)
                        .find((x) => x.orderId === detailId);
                      const assigneeId = o?.assigneeId || null;
                      const assigneeName = o?.assigneeName || null;
                      const isMine = !!me && assigneeId === me.userId;
                      const assigneeAvatar = users.find((u) => u.id === assigneeId)?.avatarUrl || o?.assigneeAvatarUrl || null;
                      return (
                        <div className="od-assignee-row">
                          {assigneeAvatar ? (
                            <img className="assignee-avatar" src={assigneeAvatar} alt="" />
                          ) : assigneeName ? (
                            <span className="assignee-avatar">{assigneeName.charAt(0).toUpperCase()}</span>
                          ) : (
                            <span className="assignee-avatar assignee-avatar--empty">?</span>
                          )}
                          {me?.isAdmin ? (
                            <select
                              className="od-inline-select"
                              value={assigneeId || ''}
                              onChange={(e) => assignOrder(detailId!, e.target.value || null)}
                            >
                              <option value="">{t('admin:unassigned')}</option>
                              {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {userDisplay(u)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <>
                              <span className={`truncate text-sm ${assigneeName ? 'font-medium text-slate-800' : 'text-slate-400'}`}>
                                {assigneeName || t('admin:unassigned')}
                              </span>
                              {!assigneeId ? (
                                <button className="ml-auto shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700" onClick={() => assignOrder(detailId!, me?.userId || null)}>
                                  {t('admin:takeOrder')}
                                </button>
                              ) : isMine ? (
                                <button className="ml-auto shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50" onClick={() => assignOrder(detailId!, null)}>
                                  {t('admin:releaseOrder')}
                                </button>
                              ) : null}
                            </>
                          )}
                        </div>
                      );
                    })()}
                    <div className="mt-3 border-t border-slate-100 pt-2.5">
                      {(() => {
                        const cur = ['Pending', 'SampleReceived', 'InProgress', 'Completed', 'Cancelled'].find(
                          (st) => st.toLowerCase() === detail.status.toLowerCase(),
                        ) || '';
                        return (
                          <select
                            className={`od-pill-select w-full${cur ? ` is-${cur.toLowerCase()}` : ''}`}
                            value={cur}
                            disabled={isUpdatingStatus === detail.orderId}
                            onChange={(e) => requestStatusChange(detail.orderId, e.target.value)}
                          >
                            {['Pending', 'SampleReceived', 'InProgress', 'Completed', 'Cancelled'].map((st) => (
                              <option key={st} value={st}>{getStatusDisplayName(st)}</option>
                            ))}
                          </select>
                        );
                      })()}
                      {(() => {
                        // Milestones from the audit trail: latest transition into
                        // InProgress («взято в роботу») and into Completed.
                        const hist = detail.history || [];
                        const lastTo = (status: string) => {
                          for (let i = hist.length - 1; i >= 0; i--) {
                            const e = hist[i];
                            if (e.type === 'Status' && (e.to || '').toLowerCase() === status) return e.at;
                          }
                          return null;
                        };
                        const takenAt = lastTo('inprogress');
                        const doneAt = lastTo('completed');
                        if (!takenAt && !doneAt) return null;
                        return (
                          <dl className="mt-2.5 flex flex-col gap-1 text-sm">
                            {takenAt && (
                              <div className="flex items-center justify-between gap-2">
                                <dt className="text-slate-500">{t('admin:detTakenAt')}</dt>
                                <dd className="font-medium text-slate-800">{formatDate(takenAt)}</dd>
                              </div>
                            )}
                            {doneAt && (
                              <div className="flex items-center justify-between gap-2">
                                <dt className="text-slate-500">{t('admin:detCompletedAt')}</dt>
                                <dd className="font-medium text-slate-800">{formatDate(doneAt)}</dd>
                              </div>
                            )}
                          </dl>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('admin:detSectionDetails')}</div>
                    <dl className="divide-y divide-slate-100 text-sm">
                      <div className="py-2">
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">{t('admin:paymentStatus')}</dt>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${detail.isPaid ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {detail.isPaid ? t('admin:paid') : t('admin:unpaid')}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="mt-1.5 w-full rounded-md px-2 py-1 text-sm font-medium text-blue-600 transition hover:bg-blue-50"
                          onClick={() => setPaid(detail.orderId, !detail.isPaid)}
                        >
                          {detail.isPaid ? t('admin:markUnpaid') : t('admin:markPaid')}
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-3 py-2">
                        <dt className="text-slate-500">{t('admin:columnOrderDate')}</dt>
                        <dd className="font-medium text-slate-800">{formatDate(detail.orderDate)}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3 py-2">
                        <dt className="text-slate-500">{t('admin:updated')}</dt>
                        <dd className="font-medium text-slate-800">{formatDate(detail.updatedAt)}</dd>
                      </div>
                    </dl>
                  </div>
                </aside>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => !creating && setShowCreate(false)}>
          <div className="modal create-order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('admin:addOrder')}</h2>
              <button className="modal-close" onClick={() => setShowCreate(false)}>
                ×
              </button>
            </div>
            <div className="confirm-body create-order-body">
              <div className="form-group">
                <label>{t('admin:coCustomerName')} *</label>
                <input
                  className="search-input"
                  value={createForm.customerName}
                  onChange={(e) => setCreateForm({ ...createForm, customerName: e.target.value })}
                />
              </div>
              <div className="co-row">
                <div className="form-group">
                  <label>{t('admin:formEmail')} *</label>
                  <input
                    className="search-input"
                    type="email"
                    value={createForm.customerEmail}
                    onChange={(e) => setCreateForm({ ...createForm, customerEmail: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin:detPhone')}</label>
                  <input
                    className="search-input"
                    value={createForm.customerPhone}
                    onChange={(e) => setCreateForm({ ...createForm, customerPhone: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('admin:coPackage')} *</label>
                <select
                  className="status-filter"
                  value={createForm.packageId}
                  onChange={(e) => {
                    const pkg = createPackages.find((p) => p.id === e.target.value);
                    setCreateForm({
                      ...createForm,
                      packageId: e.target.value,
                      price: pkg?.price != null ? String(pkg.price) : createForm.price,
                    });
                  }}
                >
                  <option value="">{t('admin:coSelectPackage')}</option>
                  {createPackages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.codes.length}){p.price != null ? ` — ${formatMoney(p.price, 'UAH')}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="co-row">
                <div className="form-group">
                  <label>{t('admin:coPrice')} *</label>
                  <input
                    className="search-input"
                    inputMode="decimal"
                    value={createForm.price}
                    onChange={(e) => setCreateForm({ ...createForm, price: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin:coQuantity')}</label>
                  <input
                    className="search-input"
                    type="number"
                    min={1}
                    value={createForm.quantity}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, quantity: Math.max(1, Number(e.target.value) || 1) })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin:filterByPayment')}</label>
                  <select
                    className="status-filter"
                    value={createForm.paymentMethod}
                    onChange={(e) => setCreateForm({ ...createForm, paymentMethod: e.target.value })}
                  >
                    <option value="invoice">{t('admin:paymentInvoice')}</option>
                    <option value="card">{t('admin:paymentCard')}</option>
                  </select>
                </div>
              </div>
              <label className="co-paid-toggle">
                <input
                  type="checkbox"
                  checked={createForm.isPaid}
                  onChange={(e) => setCreateForm({ ...createForm, isPaid: e.target.checked })}
                />
                {t('admin:coAlreadyPaid')}
              </label>
              <div className="form-group">
                <label>{t('admin:detSectionNotes')}</label>
                <textarea
                  className="search-input"
                  rows={2}
                  style={{ width: '100%', resize: 'vertical' }}
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                />
              </div>
              {selectedCreatePackage && createPrice > 0 && (
                <div className="co-total">
                  {t('admin:detPriceFinal')}:{' '}
                  <strong>{formatMoney(createPrice * (Number(createForm.quantity) || 1), 'UAH')}</strong>
                </div>
              )}
            </div>
            <div className="confirm-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)} disabled={creating}>
                {t('admin:cancel')}
              </button>
              <button className="btn btn-primary" onClick={submitCreate} disabled={creating}>
                {creating ? t('admin:saving') : t('admin:coCreate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
